import React, { useEffect } from 'react'

import { Box, Grid, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { Barcode } from 'src/components/Barcode'

import { convertGS1 } from './utils'

const useStyles = makeStyles({
  label: {
    width: '4in',
    height: '6in',
  },
  zoneA: {
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '1.5in',
    fontSize: '14px',
  },
  zoneB: {
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2.5in',
    fontSize: '14px',
  },
  zoneC: {
    borderTop: '1px solid black',
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2in',
  },
  zoneD: {
    borderTop: '1px solid black',
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    paddingLeft: '3px',
    height: '1in',
    width: '2in',
    lineHeight: '14px',
  },
  zoneE: {
    borderTop: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '4in',
  },
  zoneF: {
    borderTop: '1px solid black',
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2in',
  },
  zoneG: {
    borderTop: '1px solid black',
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2in',
  },
  zoneH: {
    borderTop: '1px solid black',
    height: '2in',
    width: '4in',
  },
  header: {
    fontSize: '12px',
    fontWeight: 'bold',
    paddingLeft: '3px',
  },
  address: {
    lineHeight: '1.0',
    paddingLeft: '10px',
  },
  from: {
    lineHeight: '1.0',
    paddingLeft: '5px',
  },
  carrier: {
    fontWeight: 'bold',
    paddingLeft: '5px',
  },
  to: {
    lineHeight: '1.0',
    paddingLeft: '10px',
  },
  container: {
    top: 'auto',
    bottom: 0,
  },
  center: {
    flexGrow: 1,
    textAlign: 'center',
  },
  inlineHeader: {
    fontWeight: 'bold',
    fontSize: '14px',
    paddingLeft: '3px',
  },
})

interface AceHardwareLabelProps {
  label_format: string
  zoneA: string[]
  zoneB: string[]
  zoneC: string
  zoneD: {
    pro: string
    bol: string
    carrier: string
    load_number: string
  }
  zoneE: {
    po: string
    sku: string
    vendor: string
  }
  zoneF: string
  zoneG: number[]
  zoneH: string
}

interface Props {
  data: AceHardwareLabelProps
  onReadyToPrint?: () => void
}

export const AceHardwareLabel = (props: Props) => {
  const { data, onReadyToPrint } = props
  const shipToBarcode = { barcode: convertGS1(data.zoneC, 3), type: 'gs1-128', displayName: '' }
  const locationBarcode = { barcode: convertGS1(data.zoneF, 3), type: 'gs1-128', displayName: '' }
  const ssccBarcode = { barcode: convertGS1(data.zoneH, 2), type: 'gs1-128', displayName: '' }
  const classes = useStyles()

  useEffect(() => {
    onReadyToPrint?.()
  }, [onReadyToPrint])

  return (
    <div className={`always-page-break ${classes.label}`}>
      <div className="print-page">
        <Box className={classes.label}>
          <Grid container spacing={0}>
            <Grid item>
              <Box className={classes.zoneA}>
                <Typography className={classes.header}>FROM</Typography>
                {data.zoneA.map((line) => (
                  <Typography key={line} className={classes.from}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneB}>
                <Typography className={classes.header}>TO</Typography>
                {data.zoneB.map((line) => (
                  <Typography key={line} className={classes.to}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneC}>
                <Grid item xs={12}>
                  <Typography className={classes.header}>SHIP TO POST</Typography>
                </Grid>
                <Box ml={6} mr={6}>
                  <Barcode barcode={shipToBarcode} scale={2} height={12} />
                </Box>
                <Box display="flex" justifyContent="center">
                  <Typography variant="h6">{shipToBarcode.barcode}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneD}>
                <Typography className={classes.header}>CARRIER</Typography>
                <Box>
                  <span className={classes.inlineHeader}>PRO: </span>
                  <span>{data.zoneD.pro}</span>
                </Box>
                <Box mb={3}>
                  <span className={classes.inlineHeader}>B/L: </span>
                  <span>{data.zoneD.bol}</span>
                </Box>
                {data.zoneD.carrier && <Box pl={1}>{data.zoneD.carrier}</Box>}
                <Box>
                  <span className={classes.inlineHeader}>Load # </span>
                  <span>{data.zoneD.load_number}</span>
                  {data.zoneD.load_number}
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneE} pt={1} pl={1}>
                <Typography variant="h2">PO#: {data.zoneE.po}</Typography>
                <Typography variant="h2">Vendor #: {data.zoneE.vendor}</Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneF}>
                <Typography className={classes.header}>FOR</Typography>
                {!!data.zoneF && (
                  <>
                    <Box ml={4} mr={4}>
                      <Barcode barcode={locationBarcode} scale={2} height={14} />
                    </Box>
                    <Box display="flex" justifyContent="center">
                      <Typography>{locationBarcode.barcode}</Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
            <Grid item>
              <Box
                className={classes.zoneG}
                display="flex"
                alignItems="center"
                flexDirection="column"
                pt={3}
              >
                <Typography variant="h4">
                  {data.label_format === 'ace_case' ? 'Carton' : 'Pallet'} #:
                </Typography>
                <Typography variant="h4">
                  {data.zoneG[0]} of {data.zoneG[1]}
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box className={`${classes.zoneH} ${classes.container}`}>
                <Typography className={classes.header}>SSCC-18</Typography>
                <Box ml={5} mr={5}>
                  <Barcode barcode={ssccBarcode} scale={2} height={20} />
                </Box>
                <Box display="flex" justifyContent="center">
                  <Typography variant="h4">{ssccBarcode.barcode}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </div>
    </div>
  )
}
